import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bike, ShoppingBag, Shield, Clock, MapPin, Star, UtensilsCrossed, Cake, ShoppingCart, PackageCheck, UserRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import InstallAppButton from "@/components/InstallAppButton";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const HERO = "https://images.unsplash.com/photo-1748540459503-19efc015143b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtZWRpdGVycmFuZWFuJTIwZm9vZCUyMGRpc2h8ZW58MHx8fHwxNzgzMTMxMDIwfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const browseAsGuest = () => {
    try { sessionStorage.setItem("talabya:guest-browse", "1"); } catch { /* ignore */ }
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="landing-page">
      {/* Nav */}
      <nav className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="طلبية" className="w-12 h-12 rounded-xl object-cover" style={{boxShadow:"0 3px 8px rgba(37,71,208,0.3)"}} />
          <div>
            <div className="font-black text-xl" style={{fontFamily:'Cairo'}}>طلبية</div>
            <div className="text-[10px] text-[var(--text-secondary)] -mt-1">Talabya</div>
          </div>
        </div>
        {user ? (
          <Link to={user.role === "driver" ? "/driver" : user.role === "admin" ? "/admin" : "/"}
            className="px-5 py-2.5 rounded-xl bg-primary-brand text-white font-bold text-sm" data-testid="go-app">
            الدخول للتطبيق
          </Link>
        ) : (
          <div className="flex gap-2 items-center">
            <LanguageSwitcher/>
            <Link to="/login" className="px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-[var(--surface-alt)]" data-testid="landing-login">{t("nav.login")}</Link>
            <Link to="/register" className="px-5 py-2.5 rounded-xl bg-primary-brand text-white font-bold text-sm talabya-shadow-float" data-testid="landing-register">{t("nav.register")}</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light text-primary-brand text-xs font-bold mb-5">
            <Star size={12} fill="currentColor"/> منصة التوصيل الأولى في جبل نفوسة
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.35] mb-5" style={{fontFamily:'Cairo'}}>
            <span className="block">كل شي تحتاجه،</span>
            <span className="block text-primary-brand mt-1">يوصلك لباب حوشك</span>
          </h1>
          <p className="text-lg text-[var(--text-secondary)] mb-8 leading-relaxed">
            مطاعم، مواد غذائية، حلويات، وأكثر — اطلب من مدن جبل نفوسة وسيصل إلى حوشك بيد <b className="text-[var(--text-primary)]">طلبية مان</b>.
            توصيل أو استلام — القرار لك.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            {user ? (
              <Link to="/" className="px-6 py-4 rounded-2xl bg-primary-brand text-white font-bold flex items-center justify-center gap-2 talabya-shadow-float hover:opacity-95" data-testid="cta-order">
                <ShoppingBag size={20}/> اطلب الآن
                <ArrowLeft size={18}/>
              </Link>
            ) : (
              <>
                <button type="button" onClick={browseAsGuest}
                  className="px-6 py-4 rounded-2xl bg-primary-brand text-white font-bold flex items-center justify-center gap-2 talabya-shadow-float hover:opacity-95"
                  data-testid="cta-guest-browse">
                  <ShoppingBag size={20}/> تصفح المحلات كزائر
                  <ArrowLeft size={18}/>
                </button>
                <Link to="/register?as=customer"
                  className="px-6 py-4 rounded-2xl bg-white border-2 border-primary-brand text-primary-brand font-bold flex items-center justify-center gap-2 hover:bg-blue-50"
                  data-testid="cta-order">
                  <UserRound size={18}/> إنشاء حساب
                </Link>
              </>
            )}
            <Link to="/register?as=driver" className="px-6 py-4 rounded-2xl bg-white border-2 border-[var(--border)] font-bold flex items-center justify-center gap-2 hover:border-primary-brand" data-testid="cta-driver">
              <Bike size={20}/> كن سائق طلبية
            </Link>
            <Link to="/download-app" className="px-6 py-4 rounded-2xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-emerald-700" data-testid="cta-download-app">
              <ShoppingBag size={20}/> حمّل التطبيق
            </Link>
          </div>

          <div className="mt-4">
            <InstallAppButton variant="cta"/>
          </div>

          <div className="flex gap-6 mt-8 text-sm">
            <div><b className="text-2xl block text-primary-brand" style={{fontFamily:'Cairo'}}>15+</b><span className="text-[var(--text-secondary)]">دقيقة توصيل</span></div>
            <div><b className="text-2xl block text-primary-brand" style={{fontFamily:'Cairo'}}>جبل نفوسة</b><span className="text-[var(--text-secondary)]">تغطية شاملة</span></div>
            <div><b className="text-2xl block text-primary-brand" style={{fontFamily:'Cairo'}}>24/7</b><span className="text-[var(--text-secondary)]">خدمة الزبائن</span></div>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-3xl overflow-hidden talabya-shadow-hover">
            {HERO && <img src={HERO} alt="طعام شهي" className="w-full h-[480px] object-cover"/>}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"/>
            <div className="absolute bottom-6 right-6 left-6 flex justify-between items-end">
              <div className="bg-white/95 backdrop-blur px-4 py-3 rounded-2xl talabya-shadow-soft flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse-dot"/>
                <div>
                  <div className="text-xs text-[var(--text-secondary)]">حالة الطلب</div>
                  <div className="font-bold text-sm">في الطريق إليك</div>
                </div>
              </div>
              <div className="bg-primary-brand text-white px-4 py-3 rounded-2xl talabya-shadow-soft">
                <div className="text-xs opacity-80">وقت الوصول</div>
                <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>12 دقيقة</div>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-6 -right-6 hidden lg:flex items-center gap-3 bg-white p-4 rounded-2xl talabya-shadow-hover w-64">
            <div className="w-12 h-12 rounded-xl bg-primary-light flex items-center justify-center text-primary-brand"><Bike size={22}/></div>
            <div>
              <div className="text-xs text-[var(--text-secondary)]">سائق قريب منك</div>
              <div className="font-bold text-sm">أحمد • ⭐ 4.9</div>
            </div>
          </div>
        </div>
      </section>

      {/* What we deliver */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-black mb-2 text-center" style={{fontFamily:'Cairo'}}>نوصّل لك كل شي</h2>
        <p className="text-center text-[var(--text-secondary)] mb-8 text-sm">من أي محل داخل جبل نفوسة — سائقنا يجيبه لعندك</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: <UtensilsCrossed size={26}/>, title: "مطاعم", desc: "وجبات ساخنة ومشاوي" },
            { icon: <ShoppingCart size={26}/>, title: "مواد غذائية", desc: "بقالة، خضار، لحوم" },
            { icon: <Cake size={26}/>, title: "حلويات", desc: "حلويات، مثلجات، معجنات" },
            { icon: <PackageCheck size={26}/>, title: "محلات أخرى", desc: "أي طلب من أي محل" },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 talabya-shadow-soft border border-[var(--border)] text-center hover:border-primary-brand transition-colors" data-testid={`landing-cat-${i}`}>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-light text-primary-brand flex items-center justify-center mb-3">{c.icon}</div>
              <h3 className="font-black text-lg" style={{fontFamily:'Cairo'}}>{c.title}</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-1">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black mb-8 text-center" style={{fontFamily:'Cairo'}}>لماذا طلبية؟</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: <Clock size={26}/>, title: "توصيل سريع", desc: "نضمن وصول طلبك خلال 30 دقيقة أو أقل داخل مدن الجبل"},
            {icon: <MapPin size={26}/>, title: "تتبع مباشر", desc: "تابع طلبك لحظة بلحظة على الخريطة من المحل حتى حوشك"},
            {icon: <Shield size={26}/>, title: "دفع آمن", desc: "ادفع نقداً عند الاستلام أو من محفظتك — كلها آمنة"},
          ].map((f,i) => (
            <div key={i} className="bg-white rounded-3xl p-6 talabya-shadow-soft border border-[var(--border)]">
              <div className="w-14 h-14 rounded-2xl bg-primary-light text-primary-brand flex items-center justify-center mb-4">{f.icon}</div>
              <h3 className="text-xl font-bold mb-2" style={{fontFamily:'Cairo'}}>{f.title}</h3>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/register?as=customer" className="group bg-white rounded-3xl p-6 talabya-shadow-soft border-2 border-transparent hover:border-primary-brand transition" data-testid="role-card-customer">
            <div className="w-14 h-14 rounded-2xl bg-primary-brand text-white flex items-center justify-center mb-4"><ShoppingBag size={24}/></div>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Cairo'}}>طلبية Eat</h3>
            <p className="text-sm text-[var(--text-secondary)]">للزبائن — تصفح المنيو، اطلب، وتابع طلبك</p>
          </Link>
          <Link to="/register?as=driver" className="group bg-white rounded-3xl p-6 talabya-shadow-soft border-2 border-transparent hover:border-primary-brand transition" data-testid="role-card-driver">
            <div className="w-14 h-14 rounded-2xl bg-[var(--secondary)] text-white flex items-center justify-center mb-4"><Bike size={24}/></div>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Cairo'}}>طلبية Man</h3>
            <p className="text-sm text-[var(--text-secondary)]">للسائقين — استلم الطلبات واكسب مع كل توصيلة</p>
          </Link>
          <Link to="/login" className="group bg-white rounded-3xl p-6 talabya-shadow-soft border-2 border-transparent hover:border-primary-brand transition" data-testid="role-card-admin">
            <div className="w-14 h-14 rounded-2xl bg-[var(--text-primary)] text-white flex items-center justify-center mb-4"><Shield size={24}/></div>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Cairo'}}>لوحة الإدارة</h3>
            <p className="text-sm text-[var(--text-secondary)]">للمطعم — إدارة الطلبات وتعيين السائقين</p>
          </Link>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[var(--border)] text-center text-sm text-[var(--text-secondary)]">
        © 2026 طلبية · التوصيل داخل جبل نفوسة 🇱🇾
      </footer>
    </div>
  );
}
